import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {HttpResponseType} from '@capacitor/core';
import {catchError, Subject, takeUntil} from 'rxjs';
import {ApiUrlService} from './api-url.service';

@Injectable({
              providedIn: 'root'
            })
export class ApiService {

  constructor(
    private http: HttpClient,
    private apiUrlService: ApiUrlService
  ) {
  }

  async getFromLocalURL(url: string): Promise<any> {
    return this.http.get<any>(url).toPromise().then((res) => res).catch(error => {
      console.error('Error', error);
      throw error;
    });
  }

  async getFromURL(url: string, params?: { [key: string]: any }, options?: {
    headers?: HttpHeaders,
    responseType?: HttpResponseType
  }): Promise<any> {
    const fullURL = params ? this.apiUrlService.apiUrl + (url ?? "") + '?' + this.serializeParams(params, 'filter_param') : this.apiUrlService.apiUrl + (url ?? "");

    return this.http.get<any>(fullURL, {
      headers:      options?.headers,
      responseType: options?.responseType as 'json'
    }).toPromise().then(
      res => {
        return res;
      }).catch(error => {
      throw error
    })
  }

  private excludedFilterParams = [ "page", "per_page", "flat_list", "locale", "include_filters", "active_module", "scope", "include_traffic_light", "with_filters", "ra_id", "only_children", "id", "group_kinds", "include_active_plans" ];

  private serializeParams(obj: {
    [key: string]: any
  }, keyPrefix = '', params: HttpParams = new HttpParams()): HttpParams {
    Object.keys(obj).forEach((key) => {
      const value  = obj[key];
      const newKey = (keyPrefix && !this.excludedFilterParams.includes(key)) ? `${ keyPrefix }[${ key }]` : key;

      // Convert double underscores to double colons
      const convertedKey = newKey.replace(/__/g, '::');

      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          // Remove the original key from the object
          delete obj[key];

          if (convertedKey.endsWith('_')) {
            const realKey = convertedKey.slice(0, -1); // Remove the trailing underscore
            value.forEach((item) => {
              if (item !== null && item !== undefined && item !== '') {
                params = params.append(realKey, item);
              }
            });
          } else {
            value.forEach((item) => {
              if (item !== null && item !== undefined) {
                params = params.append(`${ convertedKey }[]`, item);
              }
            });
          }
        } else if (typeof value === 'object') {
          if (key === 'encoder') {
            params = params.append(convertedKey, JSON.stringify(value));
          } else {
            params = this.serializeParams(value, convertedKey, params);
          }
        } else {
          params = params.append(convertedKey, value);
        }
      }
    });

    return params;
  }


  private objectToQueryString(params: { [key: string]: any }): string {
    const queryParams = Object.keys(params).map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(params[key])).join('&');

    return queryParams;
  }

  async postToURL(url: string, data: any, options?: {
    headers?: HttpHeaders,
    responseType?: HttpResponseType
  }): Promise<any> {
    return this.http.post<any>(this.apiUrlService.apiUrl + url, data, {
      headers:      options?.headers,
      responseType: options?.responseType as 'json'
    }).toPromise().then(
      (res) => res).catch(error => {
      console.error('Error', error);
      throw error;
    });
  }

  async delete(url: string): Promise<any> {
    return this.http.delete<any>(this.apiUrlService.apiUrl + url).toPromise().then(
      (res) => res).catch(error => {
      console.error('Error', error);
      throw error;
    });
  }

  async deleteWithBody(url: string, body: any): Promise<any> {
    return this.http.request<any>('DELETE', this.apiUrlService.apiUrl + url, { body }).toPromise().then(
      (res) => res).catch(error => {
      console.error('Error', error);
      throw error;
    });
  }

  async putToURL(url: string, data: any): Promise<any> {
    delete data.created_at;
    delete data.updated_at;
    return this.http.put<any>(this.apiUrlService.apiUrl + url, data).toPromise().then(
      (res) => res).catch(error => {
      console.error('Error', error);
      throw error;
    });
  }

  async patchToURL(url: string, data: any): Promise<any> {
    return this.http.patch<any>(this.apiUrlService.apiUrl + url, data).toPromise().then(
      (res) => res).catch(error => {
      console.error('Error', error);
      throw error;
    });
  }

  private cancelRequest$ = new Subject<void>();
  private currentAbortController: AbortController | null = null; // ✅ Add this property

  getFromURLAbortable(url: string, params?: { [key: string]: any }, options?: {
    headers?: HttpHeaders,
    responseType?: HttpResponseType
  }): { promise: Promise<any>, cancel: () => void } {
    
    if (this.currentAbortController) {
      this.currentAbortController.abort();
    }

   this.currentAbortController = new AbortController();

    // Cancel any previous request
    this.cancelRequest$.next(); 
    this.cancelRequest$ = new Subject<void>(); 

    const fullURL = params ? this.apiUrlService.apiUrl + url + '?' + this.serializeParams(params, 'filter_param') : this.apiUrlService.apiUrl + url;

    const observable = this.http.get<any>(fullURL, {
      headers: options?.headers,
      responseType: options?.responseType as 'json',
    }).pipe(
      takeUntil(this.cancelRequest$),
      catchError(error => {
        console.error('[API] Request failed:', error);
        throw error;
      })
    );

    const promise = observable.toPromise();

    return {
      promise,
      cancel: () => {
        console.warn(`[API] Canceling request: ${fullURL}`);
        this.cancelRequest$.next();
      }
    };
  }

  // Optional: Cancel all ongoing requests
  cancelAllRequests(): void {
    this.cancelRequest$.next();
  }

  extractPayload(token) {
    const [ algo, payload, sig ] = token.split('.')
    return JSON.parse(this.base64UrlDecode(payload))
  }

  base64UrlDecode(str) {
    // Replace URL-safe characters with standard Base64 characters
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');

    // Add necessary padding (`=`) if missing
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }

    // Decode Base64
    return decodeURIComponent(encodeURI((atob(base64))));
  }

  extractCookies() {
    const cookies = {}
    document.cookie.split("; ").forEach(
      (cookie) => {
        const [ name, value ] = cookie.split("=")
        cookies[name]         = value
      }
    )
    return cookies;
  }

  deleteCookie(cookieName: string) {
    document.cookie.split("; ").forEach(
      (cookie) => {
        const [ name, value ] = cookie.split("=")
        if (name === cookieName) {
          document.cookie = `${ name }=;  expires=Thu, 01 Jan 1970 00:00:00 UTC;`
        }
      }
    )
  }

}
